import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { usePermission } from '@/hooks/usePermission';
import { useEffectiveTenantId } from '@/hooks/useEffectiveTenantId';
import { useTenantStore } from '@/stores/tenantStore';
import { scopeVisible, type NavScope } from '@/auth/scope';
import type { Permission } from '@/auth/permissions';
import { useT, type LabelKey } from '@/i18n/labels';
import { useKnowledgeLibraryStatus } from '@/pages/knowledge-library/useKnowledgeLibraryStatus';

interface NavItem {
  to: string;
  /** i18n catalog key for the nav label (ADR-012 — localized en/he). */
  label: LabelKey;
  /**
   * Fine-grained permission required to SEE this nav item. The same map is
   * applied to the matching route guard in App.tsx so a deep link is gated
   * even when the nav item is hidden. (Replaces the old `platformOnly`
   * boolean — Wave 3b RBAC.)
   */
  requiredPermission: Permission;
  /**
   * Which plane this item belongs to (ADR-009 D7). Layered ON TOP of
   * `requiredPermission` — see {@link scopeVisible}. platform = Tenants /
   * Configuration; tenant = a specific tenant's assets; both = meaningful in
   * either plane (Dashboard, Users, Audit Log). Scope is UX, NOT the security
   * boundary (the backend is — ADR-009 D7 review S3).
   */
  scope: NavScope;
  /**
   * A server feature switch the entry additionally depends on. `knowledgeLibrary` (ADR-036): shown only when the
   * Library's `/status` says enabled — read by {@link KnowledgeLibraryNavGate}, failing closed to hidden.
   */
  featureGate?: 'knowledgeLibrary';
}

/**
 * A collapsible section header with nested nav items underneath.
 *
 * The group itself carries NO permission / scope of its own — children are
 * filtered INDIVIDUALLY with the same two-gate rule as a flat item, and the
 * group disappears entirely when nothing survives. That keeps the security
 * model unchanged (one gate per destination) while giving the nav structure.
 */
interface NavGroup {
  kind: 'group';
  label: LabelKey;
  children: NavItem[];
}

type NavEntry = NavItem | NavGroup;

const isGroup = (entry: NavEntry): entry is NavGroup =>
  (entry as NavGroup).kind === 'group';

const navEntries: NavEntry[] = [
  { to: '/', label: 'navDashboard', requiredPermission: 'dashboard.view', scope: 'both' },
  // (No Workspace entry: the portal's live-chat workspace is retired, ADR-033 D12. /workspace still
  // resolves - it redirects to the Agent Console - so old bookmarks keep working.)
  { to: '/tenants', label: 'navTenants', requiredPermission: 'tenants.manage', scope: 'platform' },
  // Users administration (Wave 3b) — top-level nav is now PLATFORM-plane only:
  // platform admins manage platform-plane users here, while tenant admins manage
  // their own users under Settings → Users. scope=platform so tenant roles no
  // longer see a top-level "Users" item.
  { to: '/users', label: 'navUsers', requiredPermission: 'users.manage', scope: 'platform' },
  // Phase C — Tenant Mind editor scoped to the active user's tenant
  // (tenantId resolves from the JWT, not the URL). Available to all
  // authenticated users with admin rights for their own tenant.
  // Label: "Tenant Mind" → "Global Mind" (OP-166 T9.7 label rewrite)
  { to: '/tenant-mind', label: 'navGlobalMind', requiredPermission: 'tenantMind.view', scope: 'tenant' },
  // Tenant Settings (WS3) — Profile | Chat Appearance | Users | CRM tabs.
  // Folds in the former standalone "CRM Settings" item (now Settings > CRM).
  { to: '/settings', label: 'navSettings', requiredPermission: 'config.edit', scope: 'tenant' },
  // (No standalone Conversation States entry: folders and tags are the "Console Settings" tab of
  // Settings, ADR-033 D8. /conversation-states still resolves - it redirects there.)
  // Knowledge Banks stays a TOP-LEVEL entry under its own name. It was briefly
  // folded into Bot Management as "Bot's Knowledge"; that was wrong on both
  // counts — banks are a first-class KB surface, not a per-bot one, and
  // "Bot's Knowledge" is the name for the memory console inside the group.
  { to: '/knowledge-banks', label: 'navKnowledgeBanks', requiredPermission: 'knowledge.manage', scope: 'tenant' },
  // ADR-036 Knowledge Library — right under Knowledge Banks, same gate, and only while the Library is switched on.
  { to: '/knowledge-library', label: 'navKnowledgeLibrary', requiredPermission: 'knowledge.manage', scope: 'tenant', featureGate: 'knowledgeLibrary' },
  // ── Bot Management ────────────────────────────────────────────────────
  // Collapsible group that replaces the former flat /agents, /channels,
  // /bot-entities and /learning entries. Those ROUTES are still registered in
  // App.tsx so existing deep links / bookmarks resolve; only the top-level nav
  // entries moved. /agents + /channels + /bot-entities are now reached as
  // in-page tabs of the /bots page ("Bot Attachments" is the new user-facing
  // name of /bot-entities).
  {
    kind: 'group',
    label: 'navBotManagement',
    children: [
      // "Bot's Knowledge" IS the memory console at /bot-mind — the four memory
      // tiers are what the bot knows. The route keeps its original path so
      // existing links hold; only the user-facing name is Bot's Knowledge.
      { to: '/bot-mind', label: 'navBotsKnowledge', requiredPermission: 'memory.manage', scope: 'tenant' },
      { to: '/learning', label: 'navLearning', requiredPermission: 'learning.review', scope: 'tenant' },
      // ADR-041 - the bot Skills library: working methods a bot follows on a turn that fits. Same gate as Knowledge Banks.
      { to: '/bot-skills', label: 'navBotSkills', requiredPermission: 'knowledge.manage', scope: 'tenant' },
      // /bots = Channels | Agents | Bot Attachments tabs.
      { to: '/bots', label: 'navBots', requiredPermission: 'bots.manage', scope: 'tenant' },
    ],
  },
  // Message Templates (OP-184) — tenant-scoped; config.edit gate matches the
  // server gate on approve/reject (admin + platformAdmin only).
  { to: '/templates', label: 'navTemplates', requiredPermission: 'config.edit', scope: 'tenant' },
  // PART B (OP-166): legacy /bots nav item removed — bare /api/bots has no gateway route (OP-164, dead)
  // raw config editor = platformAdmin-only ops tool; reuse the platformAdmin-exclusive tenants.manage gate without touching the locked permission catalog (OP-166)
  { to: '/configuration', label: 'navConfiguration', requiredPermission: 'tenants.manage', scope: 'platform' },
  { to: '/analytics', label: 'navAnalytics', requiredPermission: 'analytics.view', scope: 'both' },
  { to: '/audit', label: 'navAuditLog', requiredPermission: 'logs.view', scope: 'both' },
];

const sidebarStyle: React.CSSProperties = {
  width: 240,
  minHeight: '100vh',
  background: '#1e293b',
  color: '#e2e8f0',
  display: 'flex',
  flexDirection: 'column',
  padding: '1.5rem 0',
};

const logoStyle: React.CSSProperties = {
  padding: '0 1.25rem 1.5rem',
  fontSize: '1.125rem',
  fontWeight: 700,
  borderBottom: '1px solid #334155',
  marginBottom: '1rem',
};

const linkBaseStyle: React.CSSProperties = {
  display: 'block',
  padding: '0.625rem 1.25rem',
  color: '#94a3b8',
  textDecoration: 'none',
  fontSize: '0.875rem',
  transition: 'background 0.15s, color 0.15s',
};

const linkActiveStyle: React.CSSProperties = {
  ...linkBaseStyle,
  color: '#ffffff',
  background: '#334155',
  fontWeight: 600,
};

/** Nested child links sit one indent deeper than a top-level item. */
const childLinkBaseStyle: React.CSSProperties = {
  ...linkBaseStyle,
  padding: '0.5rem 1.25rem 0.5rem 2.25rem',
};

const childLinkActiveStyle: React.CSSProperties = {
  ...childLinkBaseStyle,
  color: '#ffffff',
  background: '#334155',
  fontWeight: 600,
};

/** Group header — a button styled to sit flush with the flat nav links. */
const groupHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '0.625rem 1.25rem',
  background: 'transparent',
  border: 'none',
  color: '#cbd5e1',
  font: 'inherit',
  fontSize: '0.875rem',
  fontWeight: 600,
  // 'start' (not 'left') so the header follows the RTL flip with the links.
  textAlign: 'start',
  cursor: 'pointer',
};

const chevronStyle: React.CSSProperties = {
  fontSize: '0.625rem',
  lineHeight: 1,
  opacity: 0.75,
};

const listResetStyle: React.CSSProperties = { listStyle: 'none', padding: 0, margin: 0 };

// ── Collapse-state persistence ─────────────────────────────────────────
// UI preference only — never anything sensitive (JWT/tenant stay in memory).

const storageKey = (label: LabelKey) => `sidebar.group.${label}`;

/** Groups DEFAULT to expanded; only an explicit 'collapsed' marker closes one. */
function readExpanded(label: LabelKey): boolean {
  try {
    return window.localStorage.getItem(storageKey(label)) !== 'collapsed';
  } catch {
    // Private-mode / disabled storage — fall back to the default.
    return true;
  }
}

function writeExpanded(label: LabelKey, expanded: boolean): void {
  try {
    window.localStorage.setItem(storageKey(label), expanded ? 'expanded' : 'collapsed');
  } catch {
    /* storage unavailable — collapse state is best-effort only */
  }
}

/** True when `pathname` is the item's route or a descendant of it. */
function matchesPath(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

const NavItemLink: React.FC<{ item: NavItem; nested?: boolean }> = ({ item, nested }) => {
  const { t } = useT();
  const base = nested ? childLinkBaseStyle : linkBaseStyle;
  const active = nested ? childLinkActiveStyle : linkActiveStyle;
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.to === '/'}
        style={({ isActive }) => (isActive ? active : base)}
      >
        {t(item.label)}
      </NavLink>
    </li>
  );
};

/**
 * ADR-036: renders its children only when the effective tenant's Knowledge Library is enabled. The status is read
 * here — mounted only for a user who already passes the entry's permission + scope gates — rather than in a
 * widely-mounted hook, and an error or a pending answer both keep the entry hidden.
 */
const KnowledgeLibraryNavGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const tenantId = useEffectiveTenantId();
  const { status } = useKnowledgeLibraryStatus(tenantId);
  return status.enabled ? <>{children}</> : null;
};

const NavGroupSection: React.FC<{ group: NavGroup; items: NavItem[] }> = ({ group, items }) => {
  const { t } = useT();
  const location = useLocation();
  const [expanded, setExpanded] = useState<boolean>(() => readExpanded(group.label));

  // Auto-expand when the active route lives inside this group, so a deep link
  // (or a route change) never leaves the current page hidden behind a collapsed
  // header. Keyed on the boolean so a MANUAL collapse while already inside the
  // group is not immediately undone.
  const containsActiveRoute = items.some((item) => matchesPath(location.pathname, item.to));
  useEffect(() => {
    if (containsActiveRoute) setExpanded(true);
  }, [containsActiveRoute]);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    writeExpanded(group.label, next);
  };

  // Disclosure pattern: the header button owns the nested list via aria-controls
  // so assistive tech can associate the two even while collapsed (WCAG 2.1 AA).
  const panelId = `nav-group-${group.label}`;

  return (
    <li>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        aria-label={`${t(group.label)}: ${t('navGroupToggleAria')}`}
        style={groupHeaderStyle}
      >
        <span>{t(group.label)}</span>
        <span aria-hidden="true" style={chevronStyle}>
          {expanded ? '▾' : '▸'}
        </span>
      </button>
      {expanded && (
        <ul id={panelId} style={listResetStyle}>
          {items.map((item) => (
            <NavItemLink key={item.to} item={item} nested />
          ))}
        </ul>
      )}
    </li>
  );
};

/** A nav entry that survived filtering, with its visible children resolved. */
type VisibleEntry =
  | { kind: 'item'; item: NavItem }
  | { kind: 'group'; group: NavGroup; items: NavItem[] };

export const Sidebar: React.FC = () => {
  const { hasPermission, isPlatformAdmin } = usePermission();
  const { t } = useT();
  // Ticket-import mapping is per-tenant. The link is built against the
  // EFFECTIVE tenant (the platform picker's selection for a platformAdmin,
  // else the user's own tenant) so it matches the route shape
  // `/tenants/:tenantId/ticket-import-mapping` AND points at the tenant the
  // operator is actually administering — not the platform home tenant.
  const authTenantId = useEffectiveTenantId();
  // ADR-009 D7 — platform admins switch planes via the tenant picker. With a
  // tenant selected they impersonate it (see tenant menus); with none, they
  // see only the platform plane.
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const hasTenantSelected = Boolean(selectedTenantId);

  const dynamicItems: NavItem[] = authTenantId
    ? [{
        to: `/tenants/${authTenantId}/ticket-import-mapping`,
        label: 'navTicketImportMapping',
        requiredPermission: 'bots.manage',
        scope: 'tenant',
      }]
    : [];

  // Two-gate visibility: the existing fine-grained permission AND the plane
  // scope (ADR-009 D7). Both must pass. Group CHILDREN run through the exact
  // same gate; a group with zero surviving children is dropped entirely.
  const itemVisible = (item: NavItem) =>
    hasPermission(item.requiredPermission) &&
    scopeVisible(item.scope, isPlatformAdmin, hasTenantSelected);

  const visibleEntries: VisibleEntry[] = [];
  for (const entry of [...navEntries, ...dynamicItems] as NavEntry[]) {
    if (isGroup(entry)) {
      const items = entry.children.filter(itemVisible);
      if (items.length > 0) visibleEntries.push({ kind: 'group', group: entry, items });
    } else if (itemVisible(entry)) {
      visibleEntries.push({ kind: 'item', item: entry });
    }
  }

  return (
    <nav style={sidebarStyle} aria-label={t('navAriaMain')}>
      <div style={logoStyle}>{t('appName')}</div>
      <ul style={listResetStyle}>
        {visibleEntries.map((entry) =>
          entry.kind === 'group' ? (
            <NavGroupSection key={`group:${entry.group.label}`} group={entry.group} items={entry.items} />
          ) : entry.item.featureGate === 'knowledgeLibrary' ? (
            <KnowledgeLibraryNavGate key={entry.item.to}>
              <NavItemLink item={entry.item} />
            </KnowledgeLibraryNavGate>
          ) : (
            <NavItemLink key={entry.item.to} item={entry.item} />
          ),
        )}
      </ul>
    </nav>
  );
};
